import {
  fetchAllModelsRequest,
  fetchModelCategoriesDiffRequest,
  fetchModelCategoriesRequest,
  fetchSingleModelRequest,
  promoteAllRequest,
  upsertModelRequest,
} from 'actions/models';
import { GlobalState } from 'reducers';
import { createSelector } from 'reselect';
import type {
  Category,
  Customer,
  MajorChange,
  MinorChange,
  MModel,
  NormalizedResource,
  Outcome,
  RuleChange,
  Selector,
  UUID,
} from 'types';
import {
  getCustomerFilter,
  getOutcomeFilter,
  getPlatformFilter,
  getStateFilter,
} from './useCasePage';

export const getFetchAllModelsLoading: Selector<boolean> = (state) =>
  state.models.loading.includes(fetchAllModelsRequest.toString());

export const getFetchSingleModelLoading: Selector<boolean> = (state) =>
  state.models.loading.includes(fetchSingleModelRequest.toString());

export const getUpsertModelLoading: Selector<boolean> = (state) =>
  state.models.loading.includes(upsertModelRequest.toString());

export const getFetchModelCategoriesLoading =
  (id: UUID): Selector<boolean> =>
  (state): boolean =>
    state.models.loading.includes(`${fetchModelCategoriesRequest.toString()}-${id}`);

export const getModel =
  (id: UUID): Selector<MModel> =>
  (state): MModel =>
    state.models.models[id];

export const getAllModels: Selector<NormalizedResource<MModel>> = (state) => state.models.models;

export const getModelCategories: Selector<Category[], [UUID]> = (state, id) => {
  const model = getModel(id)(state);
  if (!model || !model.categories || !model.categories.length) {
    return [];
  }

  return [...new Set(model.categories)]; // skip duplicates
};

export const getCategoriesCustomers: Selector<Customer[], [UUID]> = createSelector(
  [getModelCategories],
  (categories) => {
    const customers: Customer[] = [];

    categories.forEach((c) => {
      if (c.customer && !customers.find((cus) => cus.uuid === c.customer_uuid)) {
        customers.push(c.customer);
      }
    });

    return customers;
  }
);

export const getCategoriesOutcomes: Selector<Outcome[], [UUID]> = createSelector(
  [getModelCategories],
  (categories) => {
    const outcomes: Outcome[] = [];

    categories.forEach((c) => {
      if (c.outcome && !outcomes.find((o) => o.uuid === c.outcome_uuid)) {
        outcomes.push(c.outcome);
      }
    });

    return outcomes;
  }
);

export const getFilteredCategories: Selector<Category[], [UUID]> = createSelector(
  [getModelCategories, getStateFilter, getCustomerFilter, getPlatformFilter, getOutcomeFilter],
  (categories, state, customer, platform, outcome) => {
    const filteredCategories = categories
      .filter((c) => {
        if (
          state === 'staging' &&
          // @ts-ignore
          c.tenant_name === 'modeler'
        ) {
          return true;
        }
        if (
          state === 'production' &&
          // @ts-ignore
          c.tenant_name === 'human'
        ) {
          return true;
        }
        return false;
      })
      .filter((c) => !customer || c.customer_uuid === customer)
      .filter((c) => !platform || c[platform as keyof Category])
      .filter((c) => !outcome || c.outcome_uuid === outcome);

    return filteredCategories;
  }
);

export const getModelsList: Selector<MModel[]> = createSelector(
  [(state: GlobalState): GlobalState['models']['models'] => state.models.models],
  (models) => Object.values(models)
);

export const getActiveModel: Selector<UUID> = (state) => state.models.activeModel;

export const getModelsTotalCount: Selector<number> = (state) => state.models.count;

export const getPromoteAllLoading: Selector<boolean> = (state) =>
  state.models.loading.includes(promoteAllRequest.toString());

export const getModelDiffLoading: Selector<boolean> = (state) =>
  state.models.loading.includes(fetchModelCategoriesDiffRequest.toString());

export type ModelCategoriesChanges = {
  minor: Record<string, { ruleName: string; changes: MinorChange[] }>;
  major: Record<string, { ruleName: string; changes: MajorChange[] }>;
};

export const getModelCategoriesChanges: Selector<ModelCategoriesChanges, [UUID]> = createSelector(
  (state: GlobalState): Record<string, RuleChange[]> => state.models.ruleChanges,
  getModelCategories,
  (ruleChanges, categories) => {
    const changesSummary: ModelCategoriesChanges = {
      minor: {},
      major: {},
    };

    Object.entries(ruleChanges).forEach(([key, changes]) => {
      const rule = categories.find((c) => c.rule?.rule_group_uuid === key)?.rule;
      const ruleName = rule?.name || '';

      const majorChanges: MajorChange[] = [];
      const minorChanges: MinorChange[] = [];

      changes.forEach((change) => {
        if (change.kind === 'add') {
          if (change.path.includes('annotation_configs')) {
            majorChanges.push({ type: 'added_identifier' });
          } else if (change.path.includes('annotation_link_configs')) {
            majorChanges.push({ type: 'added_relationship' });
          } else if (typeof change.new !== 'string' && change.new.operator === 'ANNOTATION_MATCH') {
            majorChanges.push({ type: 'added_identifier' });
          } else {
            majorChanges.push({ type: 'added_branch' });
          }
        }

        if (change.kind === 'delete') {
          if (change.path.includes('annotation_link_configs')) {
            majorChanges.push({ type: 'removed_relationship' });
          } else if (change.path.includes('annotation_configs')) {
            majorChanges.push({ type: 'removed_identifier' });
          } else if (typeof change.old !== 'string' && change.old.operator === 'ANNOTATION_MATCH') {
            majorChanges.push({ type: 'removed_identifier' });
          } else {
            majorChanges.push({ type: 'removed_branch' });
          }
        }

        if (change.kind === 'change') {
          if (change.path.includes('annotation_link_configs')) {
            majorChanges.push({ type: 'changed_relationship' });
          } else if (change.path.includes('operator')) {
            majorChanges.push({ type: 'changed_logic' });
          }
        }
      });

      if (majorChanges.length === 0) {
        minorChanges.push({ type: 'changed_identifier', name: 'na' });
      }

      if (majorChanges.length > 0) {
        changesSummary.major[key] = { ruleName, changes: majorChanges };
      }
      if (minorChanges.length > 0) {
        changesSummary.minor[key] = { ruleName, changes: minorChanges };
      }
    });

    return changesSummary;
  }
);
